import { z } from "zod"

import { revisionSchema } from "./sync.ts"
import { tripHoldReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * Logbook periods (docs/specs/logbook-periods-plan.md, PRD R4.17). Field table: plan section 2;
 * the same columns exist in Postgres, GRDB and Room.
 */

/** The one source of the period states; Drizzle CHECKs and catalogues import or mirror it. */
export const logbookPeriodStatusSchema = z.enum(["active", "completed", "abandoned"])
export type LogbookPeriodStatus = z.infer<typeof logbookPeriodStatusSchema>

/** `ends_on` is at least `starts_on` + this many days: 12 weeks inclusive (14 Sep to 6 Dec). */
export const LOGBOOK_MIN_PERIOD_DAYS = 83

/** `YYYY-MM-DD` plus whole days, in UTC so no zone or DST can move the date. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number)
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10)
}

/**
 * `round(business / total * 10000)`, half up, in integer arithmetic (no float crosses a platform).
 * Precondition: `totalM > 0` (completion is blocked at 0); 0 gives NaN, so callers check first.
 */
export function businessUseBp(businessM: number, totalM: number): number {
  return Math.floor((businessM * 20_000 + totalM) / (2 * totalM))
}

/**
 * Stored/response shape. Lenient on purpose: no length limits or refinements, because the
 * server parses every stored row with it (CLAUDE.md, contract and schema).
 */
export const logbookPeriodSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  vehicle_id: z.uuid(),
  starts_on: z.string().date(),
  ends_on: z.string().date(),
  status: logbookPeriodStatusSchema,
  completed_at: z.string().datetime({ offset: true }).nullable(),
  /** Snapshot at completion; null while active or abandoned. */
  total_distance_m: z.number().int().nullable(),
  business_distance_m: z.number().int().nullable(),
  business_use_bp: z.number().int().nullable(),
  valid_until: z.string().date().nullable(),
  visibility: tripVisibilitySchema.default("full"),
  hold_reason: tripHoldReasonSchema.nullable().default(null),
  revision: revisionSchema,
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type LogbookPeriod = z.infer<typeof logbookPeriodSchema>

/** Postgres `integer` ceiling for the distance snapshot columns (request side only). */
const INT32_MAX = 2_147_483_647

const dateSchema = z.string().date()

/** Request payload to start a period. A new period is always `active` with no snapshot. */
export const createLogbookPeriodRequestSchema = z
  .strictObject({
    id: z.uuid().optional(),
    vehicle_id: z.uuid(),
    starts_on: dateSchema,
    /** Defaults to `starts_on` + 83 days. */
    ends_on: dateSchema.optional(),
  })
  .transform((request) => ({
    ...request,
    ends_on: request.ends_on ?? addDays(request.starts_on, LOGBOOK_MIN_PERIOD_DAYS),
  }))
  .refine((request) => request.ends_on >= addDays(request.starts_on, LOGBOOK_MIN_PERIOD_DAYS), {
    message: "ends_on must be at least 12 weeks after starts_on",
    path: ["ends_on"],
  })

export type CreateLogbookPeriodRequest = z.input<typeof createLogbookPeriodRequestSchema>
export type ParsedCreateLogbookPeriodRequest = z.infer<typeof createLogbookPeriodRequestSchema>

const SNAPSHOT_FIELDS = [
  "completed_at",
  "total_distance_m",
  "business_distance_m",
  "business_use_bp",
  "valid_until",
] as const

/**
 * Request payload to update a period. Moving `ends_on` and ending the period are the only
 * changes. Completing sets `status` and the whole snapshot in one patch (one atomic push entry).
 * Rules that need the stored row (transitions, `ends_on` against `starts_on`, `valid_until`,
 * completion date) are enforced by the sync adapter, not here: a patch alone cannot know them.
 */
export const updateLogbookPeriodRequestSchema = z
  .strictObject({
    base_revision: z.number().int().nonnegative().optional(),
    ends_on: dateSchema.optional(),
    status: z.enum(["completed", "abandoned"]).optional(),
    completed_at: z.string().datetime({ offset: true }).optional(),
    total_distance_m: z.number().int().positive().max(INT32_MAX).optional(),
    business_distance_m: z.number().int().nonnegative().max(INT32_MAX).optional(),
    business_use_bp: z.number().int().min(0).max(10_000).optional(),
    valid_until: dateSchema.optional(),
  })
  .superRefine((patch, ctx) => {
    const present = SNAPSHOT_FIELDS.filter((field) => patch[field] !== undefined)
    if (patch.status !== "completed") {
      for (const field of present) {
        ctx.addIssue({
          code: "custom",
          message: `${field} only applies to a completed period`,
          path: [field],
        })
      }
      return
    }
    for (const field of SNAPSHOT_FIELDS) {
      if (patch[field] === undefined) {
        ctx.addIssue({
          code: "custom",
          message: `${field} is required to complete a period`,
          path: [field],
        })
      }
    }
    const { total_distance_m: total, business_distance_m: business, business_use_bp: bp } = patch
    if (total === undefined || business === undefined || bp === undefined) return
    if (business > total) {
      ctx.addIssue({
        code: "custom",
        message: "business_distance_m must not exceed total",
        path: ["business_distance_m"],
      })
    } else if (bp !== businessUseBp(business, total)) {
      ctx.addIssue({
        code: "custom",
        message: "business_use_bp must equal round(business / total * 10000)",
        path: ["business_use_bp"],
      })
    }
  })

export type UpdateLogbookPeriodRequest = z.infer<typeof updateLogbookPeriodRequestSchema>
